import React from 'react';
import { Paper, Typography, Box, Stack, Chip, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Timeline as MuiTimeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
} from '@mui/lab';
import { motion } from 'framer-motion';
import { useLang } from '../../utils/i18n';

const Timeline = ({ items = [], position = 'alternate' }) => {
  const [lang] = useLang();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const localizeDate = (d) => {
    if (typeof d !== 'string') return d;
    return lang === 'es' ? d.replace(/Present/i, 'Actualidad') : d;
  };

  return (
    <MuiTimeline position={isMobile ? 'right' : position} sx={{
      '& .MuiTimelineItem-root:before': { flex: 0, padding: 0 },
      px: { xs: 0, md: 2 },
    }}>
      {items.map((item, index) => {
        const { id, date, icon, title, company, description, tags = [] } = item || {};

        return (
          <TimelineItem key={id}>
            {!isMobile && (
              <TimelineOppositeContent
                sx={{ my: 'auto', textAlign: 'right', minWidth: 140 }}
              >
                <Chip
                  label={localizeDate(date)}
                  size="small"
                  variant="outlined"
                  sx={(th) => ({
                    borderColor: alpha(th.palette.primary.main, 0.35),
                    bgcolor: alpha(th.palette.primary.main, 0.06),
                    color: th.palette.primary.main,
                    fontWeight: 600,
                  })}
                />
              </TimelineOppositeContent>
            )}

            <TimelineSeparator>
              <TimelineDot
                variant="outlined"
                sx={(th) => ({
                  borderColor: alpha(th.palette.primary.main, 0.5),
                  backgroundColor: th.palette.background.paper,
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 0 0 4px ${alpha(th.palette.primary.main, 0.08)}`,
                })}
              >
                <Box sx={(th) => ({ fontSize: 20, color: th.palette.primary.main })}>
                  {icon}
                </Box>
              </TimelineDot>
              <TimelineConnector sx={{ backgroundColor: 'rgba(255,255,255,0.06)', width: 2 }} />
            </TimelineSeparator>

            <TimelineContent sx={{ py: 1, px: 2 }}>
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -24 : 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: index * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <Paper
                  elevation={0}
                  sx={(th) => ({
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.07)',
                    backgroundColor: th.palette.background.paper,
                    transition: 'border-color .2s ease, transform .2s ease',
                    '&:hover': {
                      borderColor: alpha(th.palette.primary.main, 0.3),
                      transform: 'translateY(-2px)',
                    },
                  })}
                >
                  <Stack spacing={0.75}>
                    {isMobile && date && (
                      <Chip
                        label={localizeDate(date)}
                        size="small"
                        variant="outlined"
                        sx={(th) => ({
                          alignSelf: 'flex-start',
                          borderColor: alpha(th.palette.primary.main, 0.35),
                          bgcolor: alpha(th.palette.primary.main, 0.06),
                          color: th.palette.primary.main,
                          fontWeight: 600,
                          mb: 0.5,
                        })}
                      />
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.3 }}>
                      {title}
                    </Typography>
                    {company && (
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {company}
                      </Typography>
                    )}
                    {description && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                        {description}
                      </Typography>
                    )}
                    {Array.isArray(tags) && tags.length > 0 && (
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ pt: 0.5 }}>
                        {tags.map((t) => (
                          <Chip
                            key={t}
                            size="small"
                            label={t}
                            sx={(th) => ({
                              bgcolor: alpha(th.palette.primary.main, 0.1),
                              border: `1px solid ${alpha(th.palette.primary.main, 0.25)}`,
                              color: th.palette.primary.main,
                              fontWeight: 700,
                              fontSize: 11,
                            })}
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </motion.div>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </MuiTimeline>
  );
};

export default Timeline;
